import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Home, Users, Calendar, MessageSquare, CheckSquare, Wrench, MapPin, Phone, Car } from 'lucide-react';
import HouseOverviewTab from '@/components/houses/HouseOverviewTab';
import HouseResidentsTab from '@/components/houses/HouseResidentsTab';
import HouseRosterTab from '@/components/houses/HouseRosterTab';
import HouseLogsTab from '@/components/houses/HouseLogsTab';
import HouseTasksTab from '@/components/houses/HouseTasksTab';
import HouseMaintenanceTab from '@/components/houses/HouseMaintenanceTab';
import HouseVehiclesTab from '@/components/houses/HouseVehiclesTab';
import HouseTeamsTab from '@/components/houses/HouseTeamsTab';

export default function HouseDashboardPage() {
  const { houseId } = useParams();
  const navigate = useNavigate();
  const [house, setHouse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchHouse();
  }, [houseId]);

  const fetchHouse = async () => {
    try {
      const response = await api.get(`/houses/${houseId}`);
      setHouse(response.data.data);
    } catch (error) {
      toast.error('Failed to load house');
      navigate('/accommodation/houses');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  if (!house) {
    return null;
  }

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6" data-testid="house-dashboard-page">
      {/* Header */}
      <div>
        <Button variant="ghost" onClick={() => navigate('/accommodation/houses')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Houses
        </Button>
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">{house.name}</h1>
              <Badge variant={house.status === 'ACTIVE' ? 'default' : 'secondary'}>{house.status}</Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Home className="h-4 w-4" />
                <span>{house.house_no}</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                <span>{house.address}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="overview" className="gap-2" data-testid="house-tab-overview">
            <Home className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="residents" className="gap-2" data-testid="house-tab-residents">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Residents</span>
          </TabsTrigger>
          <TabsTrigger value="roster" className="gap-2" data-testid="house-tab-roster">
            <Calendar className="h-4 w-4" />
            <span className="hidden sm:inline">Roster</span>
          </TabsTrigger>
          <TabsTrigger value="logs" className="gap-2" data-testid="house-tab-logs">
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">House Logs</span>
          </TabsTrigger>
          <TabsTrigger value="tasks" className="gap-2" data-testid="house-tab-tasks">
            <CheckSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Tasks</span>
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="gap-2" data-testid="house-tab-maintenance">
            <Wrench className="h-4 w-4" />
            <span className="hidden sm:inline">Maintenance</span>
          </TabsTrigger>
          <TabsTrigger value="vehicles" className="gap-2" data-testid="house-tab-vehicles">
            <Car className="h-4 w-4" />
            <span className="hidden sm:inline">Vehicles</span>
          </TabsTrigger>
          <TabsTrigger value="teams" className="gap-2" data-testid="house-tab-teams">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Teams</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <HouseOverviewTab house={house} houseId={houseId} onUpdate={fetchHouse} />
        </TabsContent>

        <TabsContent value="residents">
          <HouseResidentsTab house={house} houseId={houseId} />
        </TabsContent>

        <TabsContent value="roster">
          <HouseRosterTab house={house} houseId={houseId} />
        </TabsContent>

        <TabsContent value="logs">
          <HouseLogsTab house={house} houseId={houseId} />
        </TabsContent>

        <TabsContent value="tasks">
          <HouseTasksTab house={house} houseId={houseId} />
        </TabsContent>

        <TabsContent value="maintenance">
          <HouseMaintenanceTab house={house} houseId={houseId} />
        </TabsContent>

        <TabsContent value="vehicles">
          <HouseVehiclesTab house={house} houseId={houseId} />
        </TabsContent>

        <TabsContent value="teams">
          <HouseTeamsTab houseId={houseId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
